import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { GridType, Library } from '../../../helpers/ui-interfaces'
import { hasRestriction } from '../../cell-content/restriction-icon'
import { CVEList } from '../cve-list'
import { isVersionNotWithinRange } from '../helpers'
import { SourceList, SourceType } from '../source-list'
import { SummaryBar } from '../summary-bar'
import { OverviewTabEmptyState } from './overview-tab-empty-state'
import { styles } from './styles'
import { OverviewTabPrivateState } from './overview-tab-private-state'
import { useLibrariesPageContext } from '../../../pages'
import { isPrivateLibrary } from '../../../helpers/helpers'
import { Organization } from '@contrast-security-inc/base-types'
import { Text } from '@contrast-security-inc/design-system-typography'
import { EPSSList } from '../epss-list'
import { GradeBadge } from '../../../../../components/grades'
import { GradeValue } from '../../../../../graphql/schema/types/grade-value'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { CveFilter } from '../../../../../components/cve-detail/cve-filter'
import { Vulnerability } from '../../../../../components/shared'

interface Props {
  library: Library
  applicationID?: string
  navigateToUsageTab: () => void
  organizationID: string
  organization: Organization
  gridType: GridType
}
const BoldComponent = (chunks: any) => <b>{chunks}</b>

const cveEpssContainerCss = css`
  display: grid;
  grid-template-columns: max-content max-content;
  gap: 30px;
  /* So things don't drift off-screen */
  padding-bottom: 70px;
`
const stackCss = css`
  margin-bottom: 8px;
  font-size: 16px;
`

const libraryCss = css`
  margin-bottom: 16px;
`

export const OverviewTab = ({
  library,
  applicationID,
  navigateToUsageTab,
  organizationID,
  organization,
  gridType
}: Props) => {
  const {
    invalidVersion,
    restrictedLicense,
    versionRestriction,
    restrictedByOrg
  } = library.policies

  const intl = useIntl()
  const [selectedModalVulnerability, setSelectedModalVulnerability] =
    useState<Vulnerability>()

  const { isStaticGrid } = useLibrariesPageContext()
  const isPrivate = isPrivateLibrary(library)
  const [vulnerabilityDrawerIsOpen, setVulnerabilityDrawerIsOpen] =
    useState(false)

  return (
    <>
      {!isStaticGrid && (
        <SummaryBar
          library={library}
          applicationID={applicationID}
          navigateToUsageTab={navigateToUsageTab}
          gridType={gridType}
          isPrivate={isPrivate}
        />
      )}
      <div
        className={css`
          display: flex;
          margin-top: 26px;
          margin-bottom: 30px;
        `}
      >
        <div className={styles.LibraryOverviewIssuesColumn}>
          {library.vulnerabilities.length > 0 ||
          hasRestriction({ ...library.policies }) ? (
            <>
              {library.vulnerabilities.length > 0 && (
                <div>
                  <div data-testid="libraryOverviewInformationHeader">
                    <Text as="div" className={libraryCss} font="titleH5">
                      {intl.formatMessage({
                        id: 'title_libraryUsage_library_insecure'
                      })}
                    </Text>
                  </div>
                  <div className={styles.libraryOverviewInformationBody}>
                    {intl.formatMessage({
                      id: 'description_libraryUsage_library_insecure'
                    })}
                  </div>
                  {library.remediationGuidance?.minUpgrade.version !==
                    undefined &&
                    library.remediationGuidance?.maxUpgrade.version !==
                      undefined &&
                    !isStaticGrid && (
                      <div data-testid="libraryOverviewRemediationGuidance">
                        <Text
                          as="div"
                          className={css`
                            margin-bottom: 16px;
                          `}
                          font="titleH5"
                        >
                          {intl.formatMessage({
                            id: 'title_howToFix'
                          })}
                        </Text>
                        <div className={styles.libraryOverviewInformationBody}>
                          <Stack className={stackCss}>
                            {intl.formatMessage(
                              {
                                id: 'description_remediationMinUpgrade'
                              },
                              {
                                bold: BoldComponent,
                                grade: (
                                  <GradeBadge
                                    value={
                                      library.remediationGuidance.minUpgrade
                                        .grade as GradeValue
                                    }
                                  />
                                ),
                                version:
                                  library.remediationGuidance.minUpgrade.version
                              }
                            )}
                          </Stack>
                          <Stack>
                            {intl.formatMessage(
                              {
                                id: 'description_remediationLatestStable'
                              },
                              {
                                bold: BoldComponent,
                                grade: (
                                  <GradeBadge
                                    value={
                                      library.remediationGuidance.maxUpgrade
                                        .grade as GradeValue
                                    }
                                  />
                                ),
                                version:
                                  library.remediationGuidance.maxUpgrade.version
                              }
                            )}
                          </Stack>
                        </div>
                      </div>
                    )}
                  {!!library.remediationAdvice?.closestStableVersion &&
                    !!library.remediationAdvice?.latestStableVersion &&
                    isStaticGrid && (
                      <div data-testid="libraryOverviewRemediationGuidance1">
                        <Text
                          as="div"
                          className={css`
                            margin-bottom: 16px;
                          `}
                          font="titleH5"
                        >
                          {intl.formatMessage({
                            id: 'title_howToFix'
                          })}
                        </Text>
                        <Stack className={stackCss}>
                          {intl.formatMessage(
                            {
                              id: 'description_remediationMinUpgradeStatic'
                            },
                            {
                              bold: BoldComponent,
                              grade:
                                library.remediationAdvice.closestStableVersion
                            }
                          )}
                        </Stack>
                        <Stack className={stackCss}>
                          <div
                            className={styles.libraryOverviewInformationBody}
                          >
                            {intl.formatMessage(
                              {
                                id: 'description_remediationLatestStableStatic'
                              },
                              {
                                bold: BoldComponent,
                                grade:
                                  library.remediationAdvice.latestStableVersion
                              }
                            )}
                          </div>
                        </Stack>
                      </div>
                    )}
                </div>
              )}

              <div>
                {hasRestriction({ ...library.policies }) && (
                  <Text font="titleH2" data-e2e={'libraryPolicyViolationText'}>
                    {intl.formatMessage({
                      id: 'detail_libraryPolicy_violation'
                    })}
                  </Text>
                )}

                <ul className={styles.libraryOverviewPolicyList}>
                  {restrictedByOrg && (
                    <li data-e2e={'libraryRestrictedText'}>
                      {intl.formatMessage({
                        id: 'detail_libraryRestricted_policyViolation'
                      })}
                    </li>
                  )}
                  {restrictedLicense && (
                    <li>
                      {intl.formatMessage({
                        id: 'detail_libraryRestricted_licensePolicyViolation'
                      })}
                    </li>
                  )}
                  {invalidVersion && versionRestriction === 0 && (
                    <li>
                      {intl.formatMessage({
                        id: 'detail_libraryVersion_policyViolation'
                      })}
                    </li>
                  )}
                  {isVersionNotWithinRange(library) && (
                    <li>
                      {intl.formatMessage(
                        {
                          id: 'pluralCount_libraryVersion_policyViolationRestriction'
                        },
                        { count: library.policies.versionRestriction }
                      )}
                    </li>
                  )}
                </ul>
              </div>

              <div className={cveEpssContainerCss}>
                {library.vulnerabilities.length > 0 &&
                  library.vulnerabilities.some((vulnerability) => {
                    return (
                      vulnerability.cvss3SeverityCode ||
                      vulnerability.severityCode
                    )
                  }) && (
                    <>
                      <div>
                        <Text
                          as="div"
                          className={css`
                            margin-bottom: 16px;
                          `}
                          data-testid="libraryOverviewRiskHeader"
                          font="titleH5"
                        >
                          {intl.formatMessage({ id: 'title_whatIsTheRisk' })}
                        </Text>
                        <CVEList
                          organization={organization}
                          library={library}
                          setVulnerabilityDrawerIsOpen={
                            setVulnerabilityDrawerIsOpen
                          }
                          setSelectedModalVulnerability={
                            setSelectedModalVulnerability
                          }
                        />
                      </div>
                      <EPSSList library={library} />
                    </>
                  )}
              </div>
            </>
          ) : (
            <div>
              {isPrivate ? (
                <OverviewTabPrivateState />
              ) : (
                <OverviewTabEmptyState />
              )}
            </div>
          )}
        </div>
        {!isStaticGrid && (
          <div
            className={css`
              margin-left: 5%;
            `}
          >
            <SourceList
              library={library}
              organizationID={organizationID}
              titleKey="title_applications"
              sourceType={SourceType.Applications}
            />
            <SourceList
              library={library}
              organizationID={organizationID}
              titleKey="title_servers"
              sourceType={SourceType.Servers}
            />
          </div>
        )}
      </div>
      {selectedModalVulnerability && (
        <CveFilter
          organizationUuid={organizationID}
          selectedModalVulnerability={selectedModalVulnerability}
          organization={organization}
          setVulnerabilityDrawerIsOpen={setVulnerabilityDrawerIsOpen}
          vulnerabilityDrawerIsOpen={vulnerabilityDrawerIsOpen}
          setSelected={setSelectedModalVulnerability}
        />
      )}
    </>
  )
}
